import { useState, useCallback, useEffect } from "react";
import { secureStorage, SecureStorageKeys } from "@src/services/secure-storage";
import { router } from "expo-router";
import { track } from "@visioni/a3s-analytics";
import {
  PinChangeCompleted,
  PinChangeFailed,
  PinSetupCompleted,
  PinSetupFailed,
  PinVerifyFailed,
  PinVerifySuccess,
  type PinFlow,
} from "@src/features/auth/analytics";
import { setSessionUnlocked, getPostAuthDestination } from "@src/features/auth/utils";
import { useFeatureFlags } from "@src/services/feature-flags/feature-flags";

/** Min ms to show pin verify spinner */
export const PIN_VERIFY_LOADING_MS = 400;
const INSERT_PIN_MAX_ATTEMPTS = 5;
const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

interface UsePinReturn {
  pin: number[];
  confirmPin: number[];
  insertPin: number[];
  storedPin: string | null;
  isLoadingStoredPin: boolean;
  isConfirmingPin: boolean;
  isVerifyingPin: boolean;
  onChangePin: (value: number) => void;
  onDeletePin: ({ isConfirmPin }: { isConfirmPin: boolean }) => void;
  onChangeConfirmPin: ({ value, pinString }: { value: number; pinString: string }) => void;
  onChangeInsertPin: (value: number) => void;
  onDeleteInsertPin: () => void;
  resetInsertPinError: () => void;
  error: string | null;
  insertPinError: string | null;
  insertPinFailedAttempts: number;
}

export const usePin = ({ flow = "setup" }: { flow?: PinFlow } = {}): UsePinReturn => {
  const [pin, setPin] = useState<number[]>([]);
  const [confirmPin, setConfirmPin] = useState<number[]>([]);
  const [insertPin, setInsertPin] = useState<number[]>([]);
  const [storedPin, setStoredPin] = useState<string | null>(null);
  const [isLoadingStoredPin, setIsLoadingStoredPin] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [insertPinError, setInsertPinError] = useState<string | null>(null);
  const [failedAttempts, setFailedAttempts] = useState<number>(0);
  const [insertPinFailedAttempts, setInsertPinFailedAttempts] = useState<number>(0);
  const [isConfirmingPin, setIsConfirmingPin] = useState(false);
  const [isVerifyingPin, setIsVerifyingPin] = useState(false);
  const [confirmPinPayload, setConfirmPinPayload] = useState<{ value: number; pinString: string } | null>(null);
  const { getBooleanFlag } = useFeatureFlags();

  const postAuthDestination = useCallback(() => getPostAuthDestination(getBooleanFlag), [getBooleanFlag]);

  const onChangePin = useCallback((value: number) => {
    setPin((prev) => {
      const next = [...prev, value];
      return next;
    });
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (pin.length === 4) {
        const pinString = pin.join("");
        setPin([]);
        router.push({ pathname: "/auth/confirm-pin", params: { pinString, flow } });
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [pin, flow]);

  const onDeletePin = useCallback(
    ({ isConfirmPin }: { isConfirmPin: boolean }) => {
      if (isConfirmPin && confirmPin.length > 0) {
        setConfirmPin(confirmPin.slice(0, -1));
        return;
      }
      if (pin.length > 0) {
        setPin(pin.slice(0, -1));
      }
    },
    [pin, confirmPin],
  );

  const onChangeConfirmPin = useCallback(({ value, pinString }: { value: number; pinString: string }) => {
    setConfirmPin((prev) => {
      const next = [...prev, value];
      if (next.length === 4) {
        setConfirmPinPayload({ value, pinString });
        setIsConfirmingPin(true);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (confirmPin.length !== 4 || !isConfirmingPin || !confirmPinPayload) return;

    const confirmPinString = confirmPin.join("");
    const { pinString } = confirmPinPayload;
    let cancelled = false;

    (async () => {
      try {
        const start = Date.now();

        if (confirmPinString === pinString) {
          await secureStorage.set(SecureStorageKeys.userPin, confirmPinString);
          await secureStorage.set(SecureStorageKeys.legacyUserPin, confirmPinString);
          if (cancelled) return;
          await delay(Math.max(0, PIN_VERIFY_LOADING_MS - (Date.now() - start)));
          if (cancelled) return;
          setConfirmPin([]);
          setFailedAttempts(0);
          setError(null);
          setSessionUnlocked(true);
          track(flow === "change" ? PinChangeCompleted : PinSetupCompleted);
          return router.replace(postAuthDestination());
        }

        await delay(Math.max(0, PIN_VERIFY_LOADING_MS - (Date.now() - start)));
        if (cancelled) return;

        setConfirmPin([]);
        const newFailedAttempts = failedAttempts + 1;
        setFailedAttempts(newFailedAttempts);

        const reason = newFailedAttempts >= 3 ? "max_attempts_reached" : "pin_mismatch";
        if (flow === "change") {
          track(PinChangeFailed, { step: "new_pin", reason, attempts: newFailedAttempts });
        } else {
          track(PinSetupFailed, { reason, attempts: newFailedAttempts });
        }

        if (newFailedAttempts >= 3) {
          setFailedAttempts(0);
          setError(null);
          return router.back();
        }

        setError("PINs do not match, please try again");
      } finally {
        if (!cancelled) {
          setIsConfirmingPin(false);
          setConfirmPinPayload(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [confirmPin, isConfirmingPin, confirmPinPayload, failedAttempts, postAuthDestination, flow]);

  const onChangeInsertPin = useCallback((value: number) => {
    setInsertPin((prev) => {
      const next = [...prev, value];
      if (next.length === 4) setIsVerifyingPin(true);
      return next;
    });
  }, []);

  useEffect(() => {
    if (insertPin.length !== 4 || !isVerifyingPin) return;

    const pinStr = insertPin.join("");
    let cancelled = false;

    (async () => {
      try {
        const start = Date.now();
        const stored = await secureStorage.get(SecureStorageKeys.userPin);
        if (cancelled) return;
        await delay(Math.max(0, PIN_VERIFY_LOADING_MS - (Date.now() - start)));
        if (cancelled) return;

        if (pinStr === stored) {
          setInsertPinError(null);
          setInsertPinFailedAttempts(0);
          setSessionUnlocked(true);
          track(PinVerifySuccess);
          return router.replace(postAuthDestination());
        }

        const attempts = insertPinFailedAttempts + 1;
        setInsertPinFailedAttempts(attempts);
        setInsertPin([]);
        track(PinVerifyFailed, {
          reason: attempts >= INSERT_PIN_MAX_ATTEMPTS ? "max_attempts_reached" : "wrong_pin",
          attempts,
        });
        setInsertPinError(
          attempts >= INSERT_PIN_MAX_ATTEMPTS ?
            "Too many failed attempts. Please sign in again."
          : `Wrong passcode. ${INSERT_PIN_MAX_ATTEMPTS - attempts} attempts remaining.`,
        );
      } catch {
        if (!cancelled) {
          setInsertPin([]);
          setInsertPinError("Unable to verify passcode. Please try again or sign in.");
          track(PinVerifyFailed, { reason: "storage_error" });
        }
      } finally {
        if (!cancelled) setIsVerifyingPin(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [insertPin, isVerifyingPin, insertPinFailedAttempts, postAuthDestination]);

  const onDeleteInsertPin = useCallback(() => setInsertPin((p) => p.slice(0, -1)), []);
  const resetInsertPinError = useCallback(() => setInsertPinError(null), []);

  useEffect(() => {
    secureStorage
      .get(SecureStorageKeys.userPin)
      .then(setStoredPin)
      .finally(() => setIsLoadingStoredPin(false));
  }, []);

  return {
    pin,
    confirmPin,
    insertPin,
    storedPin,
    isLoadingStoredPin,
    isConfirmingPin,
    isVerifyingPin,
    onChangePin,
    onDeletePin,
    onChangeConfirmPin,
    onChangeInsertPin,
    onDeleteInsertPin,
    resetInsertPinError,
    error,
    insertPinError,
    insertPinFailedAttempts,
  };
};
